import React, { Fragment } from 'react';
// import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import {
  allocateUserSpaceAction,
  createNewUserAction,
  deleteUserAction,
  getAllUsers,
  getOrgUserCount,
  refreshUserAction,
  resetPasswordAction,
  toggleUserActive,
  updateUserAction,
} from '../../redux/actions/userActions';
import { foldersLevelListAction, foldersListAction } from '../../redux/actions/folderActions';
import { orgsListAction } from '../../redux/actions/orgActions';
import {
  CREATE_NEW_USER_REQUEST,
  CREATE_NEW_USER_RESET,
  DELETE_USER_RESET,
  GET_ALL_USERS_RESET,
  RESET_USER_PASSWORD_RESET,
  UPDATE_USER_RESET,
  UPDATE_USER_STATUS_RESET,
} from '../../redux/constants/userConstants';
import { LIST_ORG_RESET } from '../../redux/constants/orgConstants';
import { GET_ALL_FOLDER_DATA_RESET, GET_ALL_LEVEL_FOLDER_DATA_RESET } from '../../redux/constants/folderConstants';

import Content from '../../components/Content';
import UserListTable from './UserListTable';
import AddUserModal from './AddUserModal';
import UserQuotaReachedModal from './UserQuotaReached';
import ResetPasswordModal from './ResetPasswordModal';
import DeleteUserModal from './DeleteUserModal';
import Pagination from '../../components/Pagination';
import ToggleUserActive from './ToggleUserActive';
import { validateEmail, validateOnlyChacters, validatePassword, validatePhone } from '../../utils/validators';
import { hasPermission } from '../../config/rbac';
import { roleLabel } from '../../utils/roleLabel';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
// The role catalogue is decided by the backend, not here.
//
// This screen used to fetch the raw User enum and subtract four codes locally,
// which meant every legacy file-exchange role (clientAdmin / fileAdmin /
// fileUser / fileUserOnlyDownload) and every role belonging to a module the
// tenant does not run still appeared. GET /users/roles now returns two lists —
// `assignable` (what may be given to a new user) and `filterable` (what a user
// in this tenant might already hold) — computed from the same TenantConfig
// features that gate the nav and the routes. See backend/services/
// assignableRoles.js. No exclusion list lives on the client any more, because
// a second copy of the rule is how the first one drifted.
// Label resolution moved to utils/roleLabel.js so the filter dropdown, the
// Add/Edit dropdown and the roster cell cannot answer differently.

const UsersScreen = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchKey, setSearchKey] = React.useState('');
  // const [ startValue, setStartValue ] = React.useState(1);
  // const [ endValue, setEndValue ] = React.useState(1);
  const [totalPageSize, setTotalPageSize] = React.useState(0);
  const [pageNumber, setPageNumber] = React.useState(1);
  const pageLimit = 15;
  const [selectedClient, setSelectedClient] = React.useState('');
  const [selectedRole, setSelectedRole] = React.useState('');
  // Role catalogues from the backend. `filterable` drives the list filter,
  // `assignable` drives the Add/Edit User dropdown. On a failed fetch both
  // collapse to empty rather than falling back to a hardcoded list — an empty
  // dropdown is a visible problem, a stale hardcoded one is an invisible one.
  const [filterableRoles, setFilterableRoles] = React.useState([]);
  const [assignableRoles, setAssignableRoles] = React.useState([]);
  React.useEffect(() => {
    let ignore = false;
    axios.get(`${BASE_URL}/users/roles`, { withCredentials: true })
      .then(r => {
        if (ignore) return;
        const d = r?.data || {};
        const assignable = Array.isArray(d.assignable) ? d.assignable : (Array.isArray(d.data) ? d.data : []);
        setAssignableRoles(assignable);
        setFilterableRoles(Array.isArray(d.filterable) ? d.filterable : assignable);
      })
      .catch(() => { if (!ignore) { setAssignableRoles([]); setFilterableRoles([]); } });
    return () => { ignore = true; };
  }, []);

  // Add or update user states
  const [maxUserQuotaModal, setMaxUserQuotaModal] = React.useState(false);
  const [addUserModal, setAddUserModal] = React.useState(false);
  const [userName, setUserName] = React.useState({ value: '', error: '' });
  const [userEmail, setUserEmail] = React.useState({ value: '', error: '' });
  const [userPhone, setUserPhone] = React.useState({ value: '', error: '' });
  const [userAddress, setUserAddress] = React.useState({
    value: '',
    error: '',
  });
  const [userRole, setUserRole] = React.useState({ value: '', error: '' });
  const [userSpace, setUserSpace] = React.useState({ value: 0, error: '' });
  const [userPassword, setUserPassword] = React.useState({
    value: '',
    error: '',
  });
  const [userOrg, setUserOrg] = React.useState({ value: '', error: '' });
  const [userFolders, setUserFolders] = React.useState({
    value: [],
    error: '',
  });
  const [editUserData, setEditUserData] = React.useState({});

  // Reset password states
  const [resetPasswordModal, setResetPasswordModal] = React.useState(false);
  const [resetPasswordData, setResetPasswordData] = React.useState({});
  const [resetPassword, setResetPassword] = React.useState({
    value: '',
    error: '',
  });
  const [resetPasswordConfirm, setResetPasswordConfirm] = React.useState({
    value: '',
    error: '',
  });

  // Delete user states
  const [toggleUserModal, setToggleUserModal] = React.useState(false);
  const [deleteUserModal, setDeleteUserModal] = React.useState(false);
  const [deleteUserData, setDeleteUserData] = React.useState({});
  const [deleteEmail, setDeleteEmail] = React.useState({
    value: '',
    error: '',
  });

  // Redux states
  const userLogin = useSelector(state => state.userLogin);
  const usersList = useSelector(state => state.usersList);
  const foldersList = useSelector(state => state.foldersList);
  const foldersLevelList = useSelector(state => state.foldersLevelList);
  const orgsList = useSelector(state => state.orgsList);
  const createNewUser = useSelector(state => state.createNewUser);
  const updateUser = useSelector(state => state.updateUser);
  const resetUserPassword = useSelector(state => state.resetUserPassword);
  const updateUserStatus = useSelector(state => state.updateUserStatus);
  const deleteUser = useSelector(state => state.deleteUser);

  const [orgTotalUsers, setOrgTotalUsers] = React.useState({});
  const getOrgTotalUsers = React.useCallback(
    async clientId => {
      const data = await dispatch(getOrgUserCount(clientId));
      setOrgTotalUsers(data);
    },
    [dispatch]
  );

  React.useEffect(() => {
    if (!(userLogin && userLogin.userInfo)) {
      history.push('/login');
    }
  }, [userLogin, history]);

  React.useEffect(() => {
    if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role === 'admin') {
      dispatch(orgsListAction());
    }
  }, [dispatch, addUserModal, userLogin]);

  // React.useEffect(() => {
  //   if (userOrg) dispatch(foldersListAction('folders', userOrg.value));
  // }, [userOrg, dispatch]);

  React.useEffect(() => {
    if (userOrg) dispatch(foldersLevelListAction('folders', userOrg.value));
  }, [userOrg, dispatch]);

  React.useEffect(() => {
    dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
  }, [dispatch, pageNumber, selectedClient, selectedRole, searchKey]);

  React.useEffect(() => {
    return () => {
      dispatch({ type: GET_ALL_USERS_RESET });
      dispatch({ type: LIST_ORG_RESET });
      dispatch({ type: GET_ALL_FOLDER_DATA_RESET });
      dispatch({ type: GET_ALL_LEVEL_FOLDER_DATA_RESET });
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (usersList && usersList.userInfo && usersList.userInfo.status === 'success' && usersList.userInfo.data && usersList.userInfo.data.data) {
      setTotalPageSize(usersList.userInfo.totalResults);
    }
  }, [usersList]);

  const handlePageChange = async currentPage => {
    setPageNumber(currentPage.selected + 1);
    // setStartValue(currentPage.selected + 1 * pageNumber);
    // setEndValue(startValue + pageLimit);
  };

  const allocateUserSpace = React.useCallback(
    async (userId, allowedStorage, message = 'Successful') => {
      dispatch({ type: CREATE_NEW_USER_REQUEST });
      const data = await dispatch(allocateUserSpaceAction(userId, allowedStorage));
      if (data && data.status === 'fail') {
        toast.error(data.msg);
        handleAddModalClose();
      } else if (data && data.status === 'success') {
        toast.success(message);
        handleAddModalClose();
        if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role !== 'admin') {
          getOrgTotalUsers(userLogin.userInfo.data.user.org);
        } else if (
          userLogin &&
          userLogin.userInfo &&
          userLogin.userInfo.data &&
          userLogin.userInfo.data.user &&
          userLogin.userInfo.data.user.role === 'admin' &&
          userOrg.value !== ''
        ) {
          getOrgTotalUsers(userOrg.value);
        }
      }
      dispatch({ type: CREATE_NEW_USER_RESET });
      dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
    },
    [dispatch, userLogin, userOrg, getOrgTotalUsers, searchKey]
  );

  // Add user functions
  React.useEffect(
    () => {
      if (createNewUser && createNewUser.userInfo && createNewUser.userInfo.status === 'success') {
        if (createNewUser.userInfo && createNewUser.userInfo.data && createNewUser.userInfo.data.data && createNewUser.userInfo.data.data.role === 'fileAdmin') {
          allocateUserSpace(createNewUser.userInfo.data.data._id, userSpace.value, t('screens.users.userCreated'));
        } else {
          toast.success(t('screens.users.userCreated'));
          handleAddModalClose();
          dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
        }
        dispatch({ type: CREATE_NEW_USER_RESET });
      } else if (createNewUser && !createNewUser.loading && createNewUser.error) {
        toast.error(createNewUser.error);
        dispatch({ type: CREATE_NEW_USER_RESET });
      }
    },
    //eslint-disable-next-line
    [createNewUser, dispatch, pageNumber, selectedClient, selectedRole, searchKey]
  );

  React.useEffect(
    () => {
      if (updateUser && updateUser.userInfo && updateUser.userInfo.status === 'success') {
        if (updateUser.userInfo && updateUser.userInfo.data && updateUser.userInfo.data.data && updateUser.userInfo.data.data.role === 'fileAdmin') {
          allocateUserSpace(updateUser.userInfo.data.data._id, userSpace.value, t('screens.users.userModified'));
        } else {
          toast.success(t('screens.users.userModified'));
          handleAddModalClose();
          dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
        }
        dispatch({ type: UPDATE_USER_RESET });
      } else if (updateUser && !updateUser.loading && updateUser.error) {
        toast.error(updateUser.error);
        dispatch({ type: UPDATE_USER_RESET });
      }
    },
    //eslint-disable-next-line
    [updateUser, dispatch, pageNumber, selectedClient, selectedRole, userSpace, searchKey]
  );

  React.useEffect(() => {
    if (editUserData && editUserData._id) {
      setUserName({ value: editUserData.name });
      setUserEmail({ value: editUserData.email });
      setUserPhone({ value: editUserData.phone });
      setUserAddress({ value: editUserData.address });
      setUserRole({ value: editUserData.role });
      setUserSpace({
        value: editUserData.maxStorageAllowed ? editUserData.maxStorageAllowed : 0,
      });
      setUserOrg({
        value: editUserData.org ? editUserData.org._id : '',
      });

      const folders = [];
      editUserData.uploadFolder.length > 0 && editUserData.uploadFolder.forEach(folder => folder && folders.push(folder));
      setUserFolders({ value: folders });
    }
  }, [editUserData]);

  const handleEditUserButtonClick = data => {
    setEditUserData(data);
    setAddUserModal(true);
  };

  const clearData = () => {
    setUserName({ value: '', error: '' });
    setUserEmail({ value: '', error: '' });
    setUserPhone({ value: '', error: '' });
    setUserAddress({ value: '', error: '' });
    setUserRole({ value: '', error: '' });
    setUserSpace({ value: 0, error: '' });
    setUserPassword({ value: '', error: '' });
    // setUserOrg({ value: '', error: '' });
    setUserFolders({ value: [], error: '' });
    setEditUserData({});
  };

  const handleAddModalClose = () => {
    setAddUserModal(false);
    clearData();
  };

  const handleAddFolder = id => {
    if (!userFolders.value.includes(id)) {
      setUserFolders({
        ...userFolders,
        value: [...userFolders.value, id],
        error: '',
      });
    } else {
      setUserFolders({ value: userFolders.value.filter(d => d !== id) });
    }
  };

  React.useEffect(() => {
    if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role !== 'admin') {
      setUserOrg({ value: userLogin.userInfo.data.user.org, error: '' });
      getOrgTotalUsers(userLogin.userInfo.data.user.org);
    }
  }, [userLogin, getOrgTotalUsers]);

  React.useEffect(
    () => {
      if (
        userLogin &&
        userLogin.userInfo &&
        userLogin.userInfo.data &&
        userLogin.userInfo.data.user &&
        userLogin.userInfo.data.user.role === 'admin' &&
        userOrg.value !== ''
      )
        getOrgTotalUsers(userOrg.value);
    },
    // eslint-disable-next-line
    [userLogin, userOrg, getOrgTotalUsers]
  );

  const handleAddUser = () => {
    if (userName.value === '' || userName.value.trim() === '') {
      setUserName({ ...userName, error: t('screens.users.errNameRequired') });
    } else if (!validateOnlyChacters(userName.value)) {
      setUserName({ ...userName, error: t('screens.users.errNameAlpha') });
    } else if (userEmail.value === '' || userEmail.value.trim() === '') {
      setUserEmail({ ...userEmail, error: t('screens.users.errEmailRequired') });
    } else if (!validateEmail(userEmail.value)) {
      setUserEmail({ ...userEmail, error: t('screens.users.errEmailInvalid') });
    } else if (userPhone.value === '' || userPhone.value.trim() === '') {
      setUserPhone({ ...userPhone, error: t('screens.users.errPhoneRequired') });
    } else if (!validatePhone(userPhone.value)) {
      setUserPhone({ ...userPhone, error: t('screens.users.errPhoneInvalid') });
    } else if (userAddress.value === '' || userAddress.value.trim() === '') {
      setUserAddress({ ...userAddress, error: t('screens.users.errAddressRequired') });
    } else if (userRole.value === '' || userRole.value.trim() === '') {
      setUserRole({ ...userRole, error: t('screens.users.errRoleRequired') });
    } else if (userOrg.value === '' || userOrg.value.trim() === '') {
      setUserOrg({ ...userOrg, error: t('screens.users.errOrgRequired') });
    } else if (
      (orgTotalUsers && !orgTotalUsers.customClient && orgTotalUsers.maxQuotaReached) ||
      (orgTotalUsers && orgTotalUsers.customClient && userRole.value === 'fileAdmin' && orgTotalUsers.maxAdminQuotaReached) ||
      (orgTotalUsers && orgTotalUsers.customClient && userRole.value === 'fileUserOnlyDownload' && orgTotalUsers.maxUserQuotaReached)
    ) {
      setUserRole({
        ...userRole,
        error: t('screens.users.errQuotaReached'),
      });
    } else if (userRole.value === 'fileAdmin' && userSpace.value <= 0) {
      setUserSpace({
        ...userSpace,
        error: t('screens.users.errSpaceRequired'),
      });
    } else if ((userRole.value === 'fileUser' || userRole.value === 'fileUserOnlyDownload') && userFolders.value.length === 0) {
      setUserFolders({ ...userFolders, error: t('screens.users.errFoldersRequired') });
    } else {
      if (editUserData && editUserData._id) {
        const formData = {
          name: userName.value,
          // email: userEmail.value,
          phone: userPhone.value,
          address: userAddress.value,
          role: userRole.value,
          uploadFolder: userFolders.value,
        };
        dispatch(updateUserAction(editUserData._id, formData));
      } else {
        if (!validatePassword(userPassword.value)) {
          setUserPassword({ ...userPassword, error: t('screens.users.errPasswordWeak') });
        } else {
          const formData = {
            name: userName.value,
            email: userEmail.value,
            phone: userPhone.value,
            address: userAddress.value,
            password: userPassword.value,
            passwordConfirm: userPassword.value,
            role: userRole.value,
            org: userOrg.value,
            uploadFolder: userFolders.value,
          };
          dispatch(createNewUserAction(formData));
        }
      }
    }
  };

  // Toggle User functions
  React.useEffect(() => {
    if (updateUserStatus && updateUserStatus.userInfo && updateUserStatus.userInfo.status === 'success') {
      toast.success(t('screens.users.userStatusChanged'));
      setToggleUserModal(false);
      dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
      dispatch({ type: UPDATE_USER_STATUS_RESET });
    }
  }, [updateUserStatus, pageNumber, dispatch, selectedClient, selectedRole, searchKey]);
  const handleToggleButtonClick = data => {
    setDeleteUserData(data);
    setToggleUserModal(true);
  };

  const handleUserToggle = (e, id, active) => {
    dispatch(toggleUserActive(id, !active));
  };

  const handleToggleModalClose = () => {
    setToggleUserModal(false);
    setDeleteUserData({});
  };

  // Reset password functions
  React.useEffect(() => {
    if (resetUserPassword && resetUserPassword.userInfo && resetUserPassword.userInfo.status === 'Success') {
      toast.success(t('screens.users.passwordReset'));
      handleResetPasswordModalClose();
      dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
      dispatch({ type: RESET_USER_PASSWORD_RESET });
    }
  }, [resetUserPassword, dispatch, pageNumber, selectedClient, selectedRole, searchKey]);

  const handleResetPasswordButtonClick = data => {
    setResetPasswordData(data);
    setResetPasswordModal(true);
  };

  const handleResetPassword = () => {
    if (resetPassword.value === '' || resetPassword.value.trim() === '') {
      setResetPassword({
        ...resetPassword,
        error: t('screens.users.errNewPassword'),
      });
    } else if (resetPassword.value.length < 8) {
      setResetPassword({
        ...resetPassword,
        error: t('screens.users.errPasswordLen'),
      });
    } else if (resetPasswordConfirm.value === '' || resetPasswordConfirm.value.trim() === '') {
      setResetPasswordConfirm({
        ...resetPasswordConfirm,
        error: t('screens.users.errConfirmPassword'),
      });
    } else if (resetPassword.value !== resetPasswordConfirm.value) {
      setResetPasswordConfirm({
        ...resetPasswordConfirm,
        error: t('screens.users.errPasswordMismatch'),
      });
    } else {
      dispatch(resetPasswordAction(resetPasswordData._id, resetPassword.value));
    }
  };

  const handleResetPasswordModalClose = () => {
    setResetPasswordModal(false);
    setResetPasswordData({});
    setResetPassword({ value: '', error: '' });
    setResetPasswordConfirm({ value: '', error: '' });
  };

  // Delete user functions
  React.useEffect(() => {
    if (deleteUser && deleteUser.userInfo && deleteUser.userInfo.status === 'Success') {
      toast.error(t('screens.users.userDeleted'));
      setDeleteEmail({ value: '', error: '' });
      handleDeleteUserModalClose();
      dispatch({ type: DELETE_USER_RESET });
      if (usersList && usersList.userInfo && usersList.userInfo.data && usersList.userInfo.data.data && usersList.userInfo.data.data.length === 1) {
        dispatch(getAllUsers(pageNumber - 1, pageLimit, selectedClient, selectedRole, searchKey));
      } else dispatch(getAllUsers(pageNumber, pageLimit, selectedClient, selectedRole, searchKey));
    }
  }, [usersList, pageNumber, deleteUser, dispatch, selectedClient, selectedRole, searchKey]);

  React.useEffect(() => {
    if (deleteUser && deleteUser.userInfo && deleteUser.userInfo.status === 'Success') {
      if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role !== 'admin') {
        getOrgTotalUsers(userLogin.userInfo.data.user.org);
      } else if (
        userLogin &&
        userLogin.userInfo &&
        userLogin.userInfo.data &&
        userLogin.userInfo.data.user &&
        userLogin.userInfo.data.user.role === 'admin' &&
        userOrg.value !== ''
      ) {
        getOrgTotalUsers(userOrg.value);
      }
    }
  }, [userLogin, userOrg, deleteUser, getOrgTotalUsers]);

  const handleDeleteUserButtonClick = data => {
    setDeleteUserData(data);
    setDeleteUserModal(true);
  };

  const handleDeleteUserModalClose = () => {
    setDeleteUserModal(false);
    setDeleteUserData({});
    setDeleteEmail({ value: '', error: '' });
  };

  const handleDeleteUser = () => {
    if (deleteEmail.value === '' || deleteEmail.value.trim() === '') {
      setDeleteEmail({ ...deleteEmail, error: t('screens.users.errEmailMustMatch') });
    } else if (deleteEmail.value !== deleteUserData.email) {
      setDeleteEmail({
        ...deleteEmail,
        error: t('screens.users.errEmailNoMatch'),
      });
    } else {
      dispatch(deleteUserAction(deleteUserData._id));
    }
  };

  const refreshUserData = async data => {
    if (data._id) {
      const resData = await dispatch(refreshUserAction(data._id));

      if (resData.status === 'success') {
        toast.success(resData.msg);
      } else {
        toast.error(resData.msg);
      }
    }
  };

  return (
    <Content
      currentMenu='users'
      headerTitle={t('screens.users.pageTitle')}
      soloUser={orgTotalUsers && orgTotalUsers.package && orgTotalUsers.package.metadata && orgTotalUsers.package.metadata.users}
      addBtn={hasPermission(userLogin?.userInfo?.data?.data?.role || userLogin?.userInfo?.data?.user?.role, 'users', 'create') && orgTotalUsers && !orgTotalUsers.maxQuotaReached ? true : false}
      addBtnTitle={t('screens.users.addUser')}
      addBtnClick={() =>
        userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role === 'admin'
          ? setAddUserModal(true)
          : orgTotalUsers && !orgTotalUsers.maxQuotaReached
            ? setAddUserModal(true)
            : setMaxUserQuotaModal(true)
      }
      searchBox={true}
      searchPlaceholder={t('screens.users.searchPlaceholder')}
      handleSearchChange={e => {
        setSearchKey(e.target.value);
        setPageNumber(1);
      }}
      totalReult={totalPageSize !== 0 ? t('screens.users.totalUsers', { count: totalPageSize }) : ''}
      dropdownFilter={
        userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role === 'admin' ? true : false
      }
      dropdownName='select-client'
      dropdownOptions={
        orgsList && orgsList.loading ? (
          <option value=''>{t('screens.users.loading')}</option>
        ) : (
          <Fragment>
            <option value=''>{t('screens.users.showAllUsers')}</option>
            {orgsList && orgsList.orgs && orgsList.orgs.data && orgsList.orgs.data.data && orgsList.orgs.data.data.length > 0 ? (
              orgsList.orgs.data.data.map(org => (
                <option value={org._id} key={org._id}>
                  {org.name}
                </option>
              ))
            ) : (
              <option value=''>{t('screens.users.noClientsFound')}</option>
            )}
          </Fragment>
        )
      }
      dropdownOnChange={e => {
        setSelectedClient(e.target.value);
        setPageNumber(1);
      }}
      dropdownFilter2={
        userLogin &&
          userLogin.userInfo &&
          userLogin.userInfo.data &&
          userLogin.userInfo.data.user &&
          (userLogin.userInfo.data.user.role === 'admin' || userLogin.userInfo.data.user.role === 'systemAdmin' || userLogin.userInfo.data.user.role === 'fileAdmin')
          ? true
          : false
      }
      dropdownName2='select-role'
      dropdownOptions2={
        <Fragment>
          <option value=''>{t('screens.users.showAllRoles')}</option>
          {filterableRoles.map(code => (
            <option key={code} value={code}>{roleLabel(t, code)}</option>
          ))}
        </Fragment>
      }
      dropdownOnChange2={e => {
        setSelectedRole(e.target.value);
        setPageNumber(1);
      }}
    >
      <UserListTable
        userLogin={userLogin}
        usersList={usersList}
        searchKey={searchKey}
        refreshUserData={refreshUserData}
        handleToggleButtonClick={handleToggleButtonClick}
        handleResetPasswordButtonClick={handleResetPasswordButtonClick}
        handleEditUserButtonClick={handleEditUserButtonClick}
        handleDeleteUserButtonClick={handleDeleteUserButtonClick}
        loggedinUserRole={userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role}
      />

      <div className='table__footer'>
        {/* <p className='table__footer_text'>
					Showing {startValue} - {endValue} results out of {totalPageSize} users
				</p> */}
        <p />
        <Pagination onPageChange={handlePageChange} rowsPerPage={pageLimit} totalPageSize={totalPageSize} pageNumber={pageNumber} />
      </div>

      {/* Add User Modal */}
      <AddUserModal
        addUserModal={addUserModal}
        assignableRoles={assignableRoles}
        handleAddModalClose={handleAddModalClose}
        handleAddUser={handleAddUser}
        handleAddFolder={handleAddFolder}
        createUserLoading={createNewUser.loading}
        updateUserLoading={updateUser.loading}
        role={userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role}
        totalSize={
          orgTotalUsers && orgTotalUsers.customClient
            ? orgTotalUsers.maxClientStorage
            : orgTotalUsers.package && orgTotalUsers.package.metadata && orgTotalUsers.package.metadata.space
        }
        data={{
          userName,
          setUserName,
          userEmail,
          setUserEmail,
          userPhone,
          setUserPhone,
          userAddress,
          setUserAddress,
          userRole,
          setUserRole,
          userSpace,
          setUserSpace,
          userPassword,
          setUserPassword,
          userOrg,
          setUserOrg,
          userFolders,
          orgsList,
          foldersList: foldersLevelList,
          editUserData,
        }}
      />

      <UserQuotaReachedModal maxUserQuotaModal={maxUserQuotaModal} handleMaxUserQuotaModalClose={() => setMaxUserQuotaModal(false)} />

      {/* Toggle User Modal */}
      <ToggleUserActive
        toggleUserModal={toggleUserModal}
        deleteUserData={deleteUserData}
        loading={updateUserStatus && updateUserStatus.loading}
        handleToggleModalClose={handleToggleModalClose}
        handleUserToggle={handleUserToggle}
      />

      {/* Reset Password modal */}
      <ResetPasswordModal
        resetPasswordModal={resetPasswordModal}
        loading={resetUserPassword && resetUserPassword.loading}
        handleResetPassword={handleResetPassword}
        handleResetPasswordModalClose={handleResetPasswordModalClose}
        data={{
          resetPassword,
          setResetPassword,
          resetPasswordConfirm,
          setResetPasswordConfirm,
          resetPasswordData,
        }}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        deleteUserModal={deleteUserModal}
        loading={deleteUser && deleteUser.loading}
        handleDeleteUser={handleDeleteUser}
        handleDeleteUserModalClose={handleDeleteUserModalClose}
        data={{ deleteEmail, setDeleteEmail, deleteUserData }}
      />
    </Content>
  );
};

export default UsersScreen;
